import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PartnerCredentialSummary } from "@tdelivr/api-client/generated/partner-api/partner-credential.js";

import { boundedNote } from "../board.ts";
import { PARTNER_CREDENTIALS_SCOPE } from "../navigation.ts";
import {
  CREATE_REFUSAL,
  credentialRow,
  statesPresent,
  stateOf,
  tenantSpanNote,
} from "../partner-api.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `partner` in `16 WP15 Platform Machinery.dc.html`: partner credentials and webhook delivery.
 *
 * ```
 * partner:['Partner API','API-02 · API-03','Partner credentials and webhook delivery.',
 *   [['Partners','11','done','active'],['Webhooks failing','1','failed','endpoint 500'],
 *    ['Keys expiring','2','caution','30 days'],['Deliveries today','18,402','done','']],
 *   ['Partner','Credential','Webhook'],
 *   [['Kilimani Fresh POS','key ···9F2A','delivered 99.8%','done','healthy'], …],
 *   'info','Secrets are shown once at creation, then only as the last four characters.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                  | source                                              |
 * | -------------------------------------- | --------------------------------------------------- |
 * | the rows                               | `API-02 GET /v1/platform/partner-credentials` (ADR 0052) |
 * | `Partner`                              | `credentials.displayName`. Q-196 on the difference   |
 * | `Credential` — `key ···9F2A`           | `credentials.clientIdSuffix`. Q-150, R-59            |
 * | `expires 14 days`                      | `credentials.expiresAt`                              |
 * | the `healthy` / `key expiring` badges  | `credentials.status` and `expiresAt`                 |
 * | `Webhook` — `delivered 99.8%`          | **nothing** — API-03 publishes nothing public. Q-195 |
 * | the four KPI tiles                     | **nothing** — R-04, Q-178; two of them are Q-195     |
 * | `Add a partner`                        | **nothing** — the issue command is `/internal/v1`    |
 *
 * **A platform-scoped read was not enough to finish this page, and the half that is missing is not
 * API-02's.** Two of the three columns have a source and the third is API-03's delivery outcome,
 * which that service publishes nowhere a browser can reach. ADR 0052 §6 declines to take that
 * decision on API-03's behalf — one ADR per service's decision is the established shape here — so
 * the column is drawn with the design's own `—` and the reason on it, which is what three agents
 * before this one correctly did with a column nothing maintains.
 *
 * `../partner-api.ts` is where the state reading, the expiry grammar and the mask are argued.
 */

const partnerCredentials = services.partner_api__partner_credential;

export interface PartnerApiProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Health › Partner API`, which is the design's own trail. */
  readonly crumbs: readonly string[];
  /**
   * The clock the expiry states are read against.
   *
   * Injected so the committed fidelity check can drive `key expiring` and `expired` by advancing it
   * rather than by waiting, and so a capture taken tomorrow is the same capture.
   */
  readonly now?: () => Date;
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Partner API";
/**
 * The design's own source chip, word for word: `API-02 · API-03`.
 *
 * Both are named even though only API-02 answers anything, because both own part of the page and the
 * chip is where a reader learns whose work the empty column is. `navigation.ts` lists the same two.
 */
const SOURCE = "API-02 · API-03";
const DESCRIPTION = "Partner credentials and webhook delivery.";
const COLUMNS = ["Partner", "Credential", "Webhook"] as const;
const NOTE = "Secrets are shown once at creation, then only as the last four characters.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's `FORMS.partner` primary button, word for word. */
const PRIMARY_LABEL = "Add partner";

/** The most rows one request answers, which is API-02's own maximum. */
const PER_FETCH = 200;

export function PartnerApi({ transport, session, crumbs, now }: PartnerApiProps): ReactNode {
  const [rows, setRows] = useState<readonly PartnerCredentialSummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal. ADR 0049 §3, and the notification
     * bell's three-console 403 is what happens without it: a refused read and an unreachable service
     * look identical from a browser.
     */
    if (!session.scopes.has(PARTNER_CREDENTIALS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [PARTNER_CREDENTIALS_SCOPE] });
      return;
    }

    try {
      const client = partnerCredentials.createPartnerCredentialClient(transport);
      /*
       * `listPlatformPartnerCredentialSummaries`, not `listTenantPartnerCredentialSummaries`.
       *
       * The second is "scoped to the authenticated tenant", which on this page would be one tenant's
       * keys under a heading that is a board of every partner on the platform. ADR 0052 published
       * them side by side and left the first untouched.
       */
      const page = await client.listPlatformPartnerCredentialSummaries(String(PER_FETCH));
      setRows(page.credentials);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [PARTNER_CREDENTIALS_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  // The instant the expiry states are read against, read once per render so every row on a board
  // agrees about what "expires in 14 days" means.
  const readClock: () => Date = now ?? ((): Date => new Date());
  const at = readClock();
  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((credential) => state === "" || stateOf(credential, at).label === state)
          .map((credential) => credentialRow(credential, at));

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "credentials",
    "ordered by tenant, then partner, then credential",
  );
  const span = tenantSpanNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No partner credential has been issued on any tenant yet. A row appears here when a platform engineer issues one."
      primary={{ label: PRIMARY_LABEL, refusal: CREATE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows, at)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(span === undefined ? {} : { footnote: span })}
      exportName="partner-api"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
